import {
	useConfigs,
	useIsAuthenticationOnly,
	useMandatoryUserAttributes,
	useUpdateConfigs,
	useUpdateScimConfigs
} from 'api/configurations';
import {
	EXTERNAL_ID,
	GENERIC_IDP_ID,
	GROUP,
	GuideType,
	IdPCommonInputFieldName,
	IdPOidcInputFieldName,
	IdPSamlInputFieldName,
	IdPXaaInputFieldName,
	MAPPING_FIELD_NAMES,
	Method,
	SCIMMethod,
	SPOidcDataFieldName,
	SPSamlDataFieldName,
	SPScimDataFieldName,
	SPXaaDataFieldName,
	TIdPInputField
} from 'consts';

import { useMethodUserAttributes } from 'api/attributes';
import {
	useCurrentMethodGuideFieldsInfo,
	useCurrentStepFieldsInfo,
	useIdpGuide
} from 'api/guide';
import { useCurrentGuideIdP, useCurrentIdP } from 'api/idps';
import { cloneDeep, isEmpty, isEqual, omit, uniq } from 'lodash';
import React, {
	createContext,
	PropsWithChildren,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useState
} from 'react';
import {
	IFGAGroupMapping,
	IGroupsAttributeMapping,
	IMethodConfigFields,
	IResConfigs,
	ISSOConfigFields,
	IUserAttributeMapping
} from 'types/api';
import { TFieldsValidationErrors, TMethodGuideFieldsInfo } from 'types/fields';
import { PartialRecord } from 'types/typeutils';
import {
	queryToFieldsConfigs,
	queryToScimEffectiveMethod,
	queryToScimMappingFieldsConfigs,
	queryToSpFields,
	queryXaaToXaaFields
} from 'utils/loadConfigs';
import {
	enableConfiguration,
	updateQueryMappingsWithFields,
	updateQueryOidcWithFields,
	updateQuerySamlWithFields,
	updateQueryXaaWithFields
} from 'utils/saveConfigs';
import { isEnumValue } from 'utils/enumUtils';
import {
	parseFGAMappings,
	parseSSOReBACGroupsMappings
} from 'utils/fgaParsing';
import { getFieldDefaultValue } from 'utils/fieldsConfigs';
import { loadDefaultFieldsFromGuide } from 'utils/loadGuide';
import { filterObjectByKeyMask } from 'utils/objectUtils';
import { loadPrevState, storeState } from 'utils/storage';
import { ZodEffects, ZodIssue, ZodObject } from 'zod';
import { StorageK } from 'keys';
import { useAppConfig } from 'api/appConfig';
import useToast from 'hooks/useToast';
import { FeatureFlagId } from 'types/featureflags';
import { ToastSeverity } from 'types/toast';
import {
	useCurrentGuideIdPID,
	useCurrentMethod,
	useIdPConfigContext
} from './IdPConfigContext';
import { useFeatureFlags } from './FeatureFlagsContext';
import {
	getSchemaShape,
	oidcValidationSchema,
	scimValidationSchema,
	xaaValidationSchema,
	samlValidationDualSchema,
	samlValidationLaxSchema,
	samlValidationSingleSchema,
	samlValidationStrictSchema
} from './fieldsValidation';

// An empty string counts as unset; false stays a value so a checkbox can be saved as off.
const toFieldValue = <T,>(value: T | '' | null | undefined): T | null =>
	value === '' ? null : (value ?? null);

interface ISSOConfigFieldsContextValue {
	ssoConfigs: ISSOConfigFields;
	setSSOConfigs: (config: ISSOConfigFields) => void;
	storageLoaded: boolean;
	setStorageLoaded: React.Dispatch<React.SetStateAction<boolean>>;
}

const SSOConfigFieldsContext = createContext<
	ISSOConfigFieldsContextValue | undefined
>(undefined);

interface ISSOConfigFieldsContextProviderProps extends PropsWithChildren {
	skipLoadStorage?: boolean;
}

export const SSOConfigFieldsContextProvider: React.FC<
	ISSOConfigFieldsContextProviderProps
> = ({ children = null, skipLoadStorage = false }) => {
	const [ssoConfigs, setSSOConfigs] = useState<ISSOConfigFields>({});
	const [storageLoaded, setStorageLoaded] = useState(skipLoadStorage);

	const value = useMemo(
		() => ({
			ssoConfigs,
			setSSOConfigs,
			storageLoaded,
			setStorageLoaded
		}),
		[ssoConfigs, setSSOConfigs, storageLoaded, setStorageLoaded]
	);

	return (
		<SSOConfigFieldsContext.Provider value={value}>
			{/* eslint-disable-next-line @typescript-eslint/no-use-before-define */}
			{!skipLoadStorage && <StorageSync />}
			{children}
		</SSOConfigFieldsContext.Provider>
	);
};

const useMethodConfigUserFields = () => {
	const context = useContext(SSOConfigFieldsContext);

	/* istanbul ignore next: no need to test this */
	if (!context) throw new Error('SSOConfigFieldsContextProvider is missing');

	const { ssoConfigs, setSSOConfigs } = context;

	const getMethodConfigUserFields = useCallback(
		(idpID: string, method: Method) => ssoConfigs[idpID]?.[method],
		[ssoConfigs]
	);

	const setMethodConfigUserFields = useCallback(
		(
			idpID: string,
			method: Method,
			methodConfigFields: IMethodConfigFields
		) => {
			setSSOConfigs({
				...ssoConfigs,
				[idpID]: {
					...(ssoConfigs[idpID] || {}),
					[method]: methodConfigFields
				}
			});
		},
		[setSSOConfigs, ssoConfigs]
	);

	return {
		getMethodConfigUserFields,
		setMethodConfigUserFields
	};
};

const useCurrentLoadedGuideFields = () => {
	const {
		idpID: ssoId,
		method: ssoMethod,
		guideType,
		xaaIdpID
	} = useIdPConfigContext();
	const currentMethod = useCurrentMethod();
	const isXaa = guideType === GuideType.XAA;
	// XAA guide defaults come from the selected XAA provider's template (the generic
	// idp when none is selected), like SAML/OIDC read the selected idp's template.
	const currentId = isXaa ? xaaIdpID || GENERIC_IDP_ID : ssoId;
	const { data: appConfig } = useAppConfig();
	const { data: currentIdpData } = useCurrentIdP();
	const { data: idpGuideData } = useIdpGuide(currentId);
	const { data: configsData } = useConfigs();
	const { data: mandatoryUserAttributes } =
		useMandatoryUserAttributes(currentMethod);

	const loadedGuideDefaults = useMemo(() => {
		if (!configsData || !idpGuideData) {
			return null;
		}

		if (isXaa) {
			return loadDefaultFieldsFromGuide(
				appConfig!,
				Method.Xaa,
				idpGuideData,
				mandatoryUserAttributes || []
			);
		}

		if (
			!ssoId ||
			!ssoMethod ||
			idpGuideData?.id !== ssoId ||
			currentIdpData?.id !== ssoId
		) {
			return null;
		}

		return loadDefaultFieldsFromGuide(
			appConfig!,
			ssoMethod,
			idpGuideData,
			mandatoryUserAttributes || []
		);
	}, [
		appConfig,
		configsData,
		isXaa,
		ssoId,
		currentIdpData?.id,
		ssoMethod,
		idpGuideData,
		mandatoryUserAttributes
	]);

	// remove when formik is dead
	const ready = useMemo(() => !!loadedGuideDefaults, [loadedGuideDefaults]);

	const getFieldValueFromGuide = useCallback(
		<K extends keyof IMethodConfigFields>(
			fieldName: K
		): IMethodConfigFields[K] =>
			toFieldValue(loadedGuideDefaults?.[fieldName]) as IMethodConfigFields[K],
		[loadedGuideDefaults]
	);

	return {
		getFieldValueFromGuide,
		ready
	};
};

/**
 * The method whose configuration stores the *mapping* fields being edited. In
 * SSO guides this is `useCurrentMethod`. In SCIM guides, explicit SCIM methods
 * map directly, while generic SCIM guides mirror the backend's enabled-first
 * resolution of which method's configuration drives SCIM mapping at runtime.
 *
 * Scoped to mapping fields on purpose: the backend resolves the whole
 * SAML+OIDC configuration pair from either id, so everything that identifies
 * the configuration (SCIM base URL, SCIM key, `scimProviderID`) stays on
 * `useCurrentMethod` and remains one consistent config-pair story.
 */
const useCurrentEffectiveMethod = (): Method => {
	const { guideType, scimMethod } = useIdPConfigContext();
	const baseMethod = useCurrentMethod();
	const { data: configsData } = useConfigs();

	return useMemo(() => {
		if (
			guideType === GuideType.SCIM &&
			(!scimMethod || scimMethod === SCIMMethod.Generic) &&
			configsData
		) {
			return queryToScimEffectiveMethod(configsData);
		}
		return baseMethod;
	}, [baseMethod, configsData, guideType, scimMethod]);
};

/**
 * Like `useCurrentEffectiveMethod`, but keeps the SSO method nullable (no
 * SAML fallback) so components can gate on "no method selected yet" in SSO
 * guides.
 */
export const useCurrentGuideMethod = () => {
	const { guideType, method } = useIdPConfigContext();
	const effectiveMethod = useCurrentEffectiveMethod();
	if (guideType === GuideType.SCIM) {
		return effectiveMethod;
	}
	// XAA is a standalone config whose method is always Xaa once in its guide.
	if (guideType === GuideType.XAA) {
		return Method.Xaa;
	}
	return method;
};

const useCurrentLoadedConfigFields = () => {
	const { data: configsData } = useConfigs();
	const { idpID, guideType, xaaIdpID } = useIdPConfigContext();
	const method = useCurrentEffectiveMethod();
	// the sp fields identify the configuration (SCIM base URL, ACS/callback
	// URLs), so they stay on the method the SCIM key and `scimProviderID` are
	// bound to - only the mapping fields follow the effective method
	const spFieldsMethod = useCurrentMethod();

	// XAA is a standalone config: it must load its issuer regardless of whether SAML/OIDC
	// is also enabled (queryToFieldsConfigs prefers SAML/OIDC by priority order).
	const [loadedIdpId, loadedMethod, loadedMethodConfigFields] = useMemo(() => {
		if (!configsData) {
			return [];
		}
		if (method === Method.Xaa) {
			// Mirror SSO/OIDC: only surface a *loaded* XAA config once it is actually
			// enabled. On a fresh (not-yet-enabled) XAA config queryXaaToXaaFields returns an
			// empty UserAttributes array, which would mask the guide-default mappings
			// (e.g. sub->externalID, email, name, phone_number) via the loaded ?? guide merge
			// in getFieldValue. SSO avoids this because queryToFieldsConfigs returns empty
			// until the config is enabled - do the same here so XAA seeds its guide defaults.
			// An enabled-but-empty config (no issuers) is treated the same: fall through to
			// the guide defaults rather than loading an empty issuer that masks them.
			const issuers = configsData.xaa?.jwtBearerSettings?.issuers ?? {};
			return configsData.xaa?.enabled && Object.keys(issuers).length > 0
				? queryXaaToXaaFields(configsData)
				: [];
		}
		if (guideType === GuideType.SCIM) {
			return queryToScimMappingFieldsConfigs(configsData, method);
		}
		return queryToFieldsConfigs(configsData);
	}, [configsData, guideType, method]);

	const spFields = useMemo(() => {
		if (!configsData) {
			return {};
		}
		return queryToSpFields(configsData, spFieldsMethod);
	}, [configsData, spFieldsMethod]);

	const idpIdToUse =
		guideType === GuideType.XAA ? xaaIdpID || GENERIC_IDP_ID : idpID;

	// remove when formik is dead
	const ready = useMemo(() => !!spFields, [spFields]);

	const getFieldValueFromLoadedConfig = useCallback(
		<K extends keyof IMethodConfigFields>(
			fieldName: K
		): IMethodConfigFields[K] => {
			if (
				isEnumValue(SPSamlDataFieldName, fieldName) ||
				isEnumValue(SPOidcDataFieldName, fieldName) ||
				isEnumValue(SPScimDataFieldName, fieldName) ||
				isEnumValue(SPXaaDataFieldName, fieldName)
			) {
				return spFields[fieldName];
			}

			// SCIM guides read the stored mapping fields regardless of which IdP
			// the SSO configuration was set up with
			if (
				method !== loadedMethod ||
				(guideType !== GuideType.SCIM && idpIdToUse !== loadedIdpId)
			) {
				return null;
			}
			return toFieldValue(loadedMethodConfigFields?.[fieldName]);
		},
		[
			guideType,
			idpIdToUse,
			loadedIdpId,
			loadedMethod,
			loadedMethodConfigFields,
			method,
			spFields
		]
	);

	return {
		getFieldValueFromLoadedConfig,
		ready
	};
};

export const useSSOConfigCurrentFieldsInitialized = () => {
	const { guideType } = useIdPConfigContext();
	const { data: configsData } = useConfigs();
	const { ready: guideDefaultsReady } = useCurrentLoadedGuideFields();
	const { ready: configFieldsReady } = useCurrentLoadedConfigFields();

	if (guideType === GuideType.SCIM) {
		// mapping components in SCIM guides read the loaded configuration, and
		// generic SCIM guides resolve their effective method from it
		return !!configsData;
	}
	return guideDefaultsReady && configFieldsReady;
};

export const useSSOConfigFields = () => {
	const { getMethodConfigUserFields, setMethodConfigUserFields } =
		useMethodConfigUserFields();
	const { getFieldValueFromGuide } = useCurrentLoadedGuideFields();
	const { getFieldValueFromLoadedConfig } = useCurrentLoadedConfigFields();
	const { guideType, isSubTenant } = useIdPConfigContext();
	const { data: currentIdP } = useCurrentGuideIdP();
	const currentMethod = useCurrentEffectiveMethod();

	// useCurrentGuideIdPID resolves the XAA content idp for XAA guides and the
	// SCIM idp for SCIM guides, so XAA drafts land in the selected XAA idp's bucket.
	const guideIdpID = useCurrentGuideIdPID();
	// SCIM guide drafts live in their own bucket so pending SSO-guide edits
	// for the same IdP + method are never picked up (or discarded) by the
	// SCIM save flow
	const idpIdToUse =
		guideType === GuideType.SCIM && guideIdpID
			? `scim:${guideIdpID}`
			: guideIdpID;

	const getCurrentMethodConfigUserFields = useCallback(() => {
		if (!idpIdToUse || !currentMethod) return null;
		return getMethodConfigUserFields(idpIdToUse, currentMethod);
	}, [getMethodConfigUserFields, idpIdToUse, currentMethod]);

	const getFieldValue = useCallback(
		<K extends keyof IMethodConfigFields>(
			fieldName: K,
			options?: { ignoreUserValues?: boolean }
		): IMethodConfigFields[K] => {
			const currentUserFields = getCurrentMethodConfigUserFields();

			const userFieldValue = currentUserFields?.[fieldName];
			const loadedFieldValue = getFieldValueFromLoadedConfig(fieldName);
			const guideFieldValue = getFieldValueFromGuide(fieldName);
			const defaultFieldValue = getFieldDefaultValue(fieldName, currentIdP!);

			const val = ((options?.ignoreUserValues ? null : userFieldValue) ??
				loadedFieldValue ??
				guideFieldValue ??
				defaultFieldValue) as any;

			return val;
		},
		[
			currentIdP,
			getFieldValueFromGuide,
			getCurrentMethodConfigUserFields,
			getFieldValueFromLoadedConfig
		]
	);

	const setFieldValue = useCallback(
		<K extends keyof IMethodConfigFields>(
			fieldName: K,
			val: IMethodConfigFields[K]
		) => {
			if (!idpIdToUse || !currentMethod) return;
			const currentUserFields = getCurrentMethodConfigUserFields();

			setMethodConfigUserFields(idpIdToUse, currentMethod, {
				...currentUserFields,
				[fieldName]: val
			});
		},
		[
			currentMethod,
			getCurrentMethodConfigUserFields,
			idpIdToUse,
			setMethodConfigUserFields
		]
	);

	const clearFields = useCallback(() => {
		if (!idpIdToUse || !currentMethod) return;

		setMethodConfigUserFields(idpIdToUse, currentMethod, {});
	}, [currentMethod, idpIdToUse, setMethodConfigUserFields]);

	const clearFieldValues = useCallback(
		(fieldNames: readonly (keyof IMethodConfigFields)[]) => {
			if (!idpIdToUse || !currentMethod) return;

			setMethodConfigUserFields(
				idpIdToUse,
				currentMethod,
				omit(getCurrentMethodConfigUserFields(), ...fieldNames)
			);
		},
		[
			currentMethod,
			getCurrentMethodConfigUserFields,
			idpIdToUse,
			setMethodConfigUserFields
		]
	);

	return {
		setFieldValue,
		getFieldValue,
		getAllFields: getCurrentMethodConfigUserFields,
		clearFields,
		clearFieldValues,
		isSubTenant
	};
};

export const useSSOConfigMappings = () => {
	const { getFieldValue, setFieldValue, isSubTenant } = useSSOConfigFields();
	const { data: configsData } = useConfigs();
	const { data: authenticationOnly } = useIsAuthenticationOnly();

	return useMemo(
		() => ({
			isSubTenant,
			getUserAttributesMappings: () =>
				getFieldValue(IdPCommonInputFieldName.UserAttributes),

			setUserAttributesMappings: (
				userAttributesMappings: IUserAttributeMapping[]
			) => {
				setFieldValue(
					IdPCommonInputFieldName.UserAttributes,
					userAttributesMappings as any
				);
			},

			getGroupsAttributeMappings: () =>
				getFieldValue(IdPCommonInputFieldName.GroupsAttributeMapping),

			setGroupsAttributeMappings: (
				groupsAttributeMappings: IGroupsAttributeMapping[]
			) => {
				setFieldValue(
					IdPCommonInputFieldName.GroupsAttributeMapping,
					groupsAttributeMappings as any
				);
			},

			getGroupsAttributeName: () =>
				getFieldValue(IdPCommonInputFieldName.GroupsAttribute),

			setGroupsAttributeName: (groupsAttributeName: IUserAttributeMapping) => {
				setFieldValue(
					IdPCommonInputFieldName.GroupsAttribute,
					groupsAttributeName
				);
			},
			// Validation enforces no mandatory attributes on an authentication-only connection, so
			// marking the field required would show an asterisk an empty value still satisfies.
			isGroupsAttributeRequired: () =>
				(!authenticationOnly &&
					configsData?.saml.mandatoryUserAttributes?.includes(GROUP)) ||
				false,

			getDefaultSSORoles: () =>
				getFieldValue(IdPCommonInputFieldName.DefaultSSORoles),

			setDefaultSSORoles: (defaultSSORoles: string[]) => {
				setFieldValue(IdPCommonInputFieldName.DefaultSSORoles, defaultSSORoles);
			},

			getFGAMappings: () =>
				parseSSOReBACGroupsMappings(
					getFieldValue(IdPCommonInputFieldName.RebacGroupsMappings) as any
				),

			setFGAMappings: (fgaMappings: IFGAGroupMapping[] | undefined) => {
				setFieldValue(
					IdPCommonInputFieldName.RebacGroupsMappings,
					parseFGAMappings(fgaMappings) as any
				);
			}
		}),
		[
			getFieldValue,
			setFieldValue,
			isSubTenant,
			authenticationOnly,
			configsData?.saml.mandatoryUserAttributes
		]
	);
};

export const useCurrentSCIMMethod = () => {
	const { scimMethod } = useIdPConfigContext();
	return scimMethod || SCIMMethod.Generic;
};

export const useSaveScim = () => {
	const { scimIdpID } = useIdPConfigContext();
	const method = useCurrentEffectiveMethod();
	// `scimProviderID` is part of the configuration's identity, not its mapping:
	// the SCIM URL shown in the guide and the SCIM key minted beside it both
	// resolve generic guides via `useCurrentMethod`, so the provider id has to
	// land on the same configuration or the key-status reads never see it
	const providerIDMethod = useCurrentMethod();
	const updateConfigsScimMutation = useUpdateScimConfigs();
	const { data: configsData } = useConfigs();
	const { data: userAttributesData } = useMethodUserAttributes(method);
	const { getFieldValue, getAllFields, clearFieldValues } =
		useSSOConfigFields();
	const { getFieldValueFromLoadedConfig } = useCurrentLoadedConfigFields();
	const { addToast } = useToast();

	const saveScimConfig = useCallback(async () => {
		/* istanbul ignore if: no need to test this */
		if (!scimIdpID || !configsData) {
			return false;
		}

		try {
			const userFields = getAllFields() || {};
			const hasMappingEdits = MAPPING_FIELD_NAMES.some(
				(fieldName) => fieldName in userFields
			);
			if (hasMappingEdits && !userAttributesData) {
				// the user attributes failed to load, so the mapping edits cannot
				// be serialized — surface the same feedback as a failed save
				// request so Finish is never a silent no-op
				addToast({
					severity: ToastSeverity.Error,
					content: intl.tr('scimConfig.save.saveErrorMessage')
				});
				return false;
			}

			let updatedConfig: IResConfigs;
			if (hasMappingEdits) {
				// SCIM consumes only the custom-attributes portion of the user
				// mapping — keep the stored standard mappings as-is and take only
				// custom-attribute rows from the guide's edits
				const customAttributeValues = new Set(
					userAttributesData!
						.filter((userAttribute) => userAttribute.custom)
						.map((userAttribute) => userAttribute.value)
				);
				const storedUserMappings =
					getFieldValueFromLoadedConfig(
						IdPCommonInputFieldName.UserAttributes
					) || [];
				const editedUserMappings =
					getFieldValue(IdPCommonInputFieldName.UserAttributes) || [];
				const userAttributesMappings = [
					...storedUserMappings.filter(
						({ spAttr }) => spAttr && !customAttributeValues.has(spAttr)
					),
					...editedUserMappings.filter(
						({ spAttr }) => spAttr && customAttributeValues.has(spAttr)
					)
				];

				updatedConfig = updateQueryMappingsWithFields(
					{
						...Object.fromEntries(
							MAPPING_FIELD_NAMES.map((fieldName) => [
								fieldName,
								getFieldValue(fieldName) || null
							])
						),
						[IdPCommonInputFieldName.UserAttributes]: userAttributesMappings,
						// the groups attribute name is hidden in SCIM guides, so it is
						// resolved from the stored config only — never from guide
						// defaults, which would silently switch on assertion-based
						// group mapping (samlMappings.group) the admin never set
						[IdPCommonInputFieldName.GroupsAttribute]:
							getFieldValueFromLoadedConfig(
								IdPCommonInputFieldName.GroupsAttribute
							) || null
					} as IMethodConfigFields,
					configsData,
					userAttributesData!,
					method
				);
			} else {
				updatedConfig = cloneDeep(configsData);
			}

			if (providerIDMethod === Method.Saml) {
				updatedConfig.saml = {
					...updatedConfig.saml,
					scimProviderID: scimIdpID
				};
			} else {
				const tenantId = updatedConfig.tenant.id;
				updatedConfig.oauth = {
					...updatedConfig.oauth,
					providerSettings: {
						...updatedConfig.oauth.providerSettings,
						[tenantId]: {
							...updatedConfig.oauth.providerSettings[tenantId],
							scimProviderID: scimIdpID
						}
					}
				};
			}
			await updateConfigsScimMutation.mutateAsync(updatedConfig);
			if (hasMappingEdits) {
				clearFieldValues(MAPPING_FIELD_NAMES);
			}
			return true;
		} catch (err) {
			return false;
		}
	}, [
		addToast,
		clearFieldValues,
		configsData,
		getAllFields,
		getFieldValue,
		getFieldValueFromLoadedConfig,
		method,
		providerIDMethod,
		scimIdpID,
		updateConfigsScimMutation,
		userAttributesData
	]);

	return useMemo(
		() =>
			scimIdpID && configsData ? { saveScimConfig } : { saveScimConfig: null },
		[scimIdpID, configsData, saveScimConfig]
	);
};

// Opt-in: values an IdP or our backend matches verbatim, where surrounding whitespace is only
// ever a paste artifact. An unlisted field keeps exactly what the user typed, which is what
// client-secret needs -- trimming a credential would silently rewrite it.
// Trimming here rather than on change keeps spaces typable mid-value, and reaches every
// consumer: validation, save, and the unsaved config sent to the SSO test.
const TRIMMED_FIELDS = new Set<string>([
	IdPSamlInputFieldName.IdPSsoUrl,
	IdPSamlInputFieldName.IdPIssuer,
	IdPSamlInputFieldName.IdPCertificate,
	IdPSamlInputFieldName.IdPMetadataUrl,
	IdPOidcInputFieldName.ProviderName,
	IdPOidcInputFieldName.ClientId,
	IdPOidcInputFieldName.GrantType,
	IdPOidcInputFieldName.Issuer,
	IdPOidcInputFieldName.AuthorizationEndpoint,
	IdPOidcInputFieldName.TokenEndpoint,
	IdPOidcInputFieldName.UserInfoEndpoint,
	IdPOidcInputFieldName.JWKsEndpoint
]);

const trimFieldValue = (fieldName: string, value: any) =>
	TRIMMED_FIELDS.has(fieldName) && typeof value === 'string'
		? value.trim()
		: value;

export const useCurrentComputedFields = (ignoreUserValues = false) => {
	const { getFieldValue } = useSSOConfigFields();
	const { guideType } = useIdPConfigContext();
	const method = useCurrentMethod();

	const computedFields: IMethodConfigFields = useMemo(() => {
		/* istanbul ignore if: no need to test this */
		if (!guideType) {
			return {};
		}

		// A SCIM guide owns the mapping fields and nothing else - the connection fields belong to
		// the SSO configuration it borrows the mapping from. Computing the full set would surface
		// connection-field errors on a guide that never renders those fields.
		if (guideType === GuideType.SCIM) {
			return Object.fromEntries(
				MAPPING_FIELD_NAMES.map((fieldName) => [
					fieldName,
					getFieldValue(fieldName, { ignoreUserValues }) || null
				])
			);
		}

		const fieldNames: (keyof IMethodConfigFields)[] = Object.values(
			IdPCommonInputFieldName
		);
		if (method === Method.Saml) {
			fieldNames.push(...Object.values(IdPSamlInputFieldName));
		}
		if (method === Method.Oidc) {
			fieldNames.push(...Object.values(IdPOidcInputFieldName));
		}
		if (method === Method.Xaa) {
			fieldNames.push(...Object.values(IdPXaaInputFieldName));
		}
		return Object.fromEntries(
			fieldNames.map((fieldName) => [
				fieldName,
				// empty strings become null so they leave the dirty state unaffected
				toFieldValue(
					trimFieldValue(
						fieldName,
						getFieldValue(fieldName, { ignoreUserValues })
					)
				)
			])
		);
	}, [getFieldValue, guideType, ignoreUserValues, method]);

	return { computedFields };
};

const areAllSamlFieldPresent = (record: PartialRecord<TIdPInputField, any>) =>
	IdPSamlInputFieldName.IdPMetadataUrl in record &&
	IdPSamlInputFieldName.IdPSsoUrl in record &&
	IdPSamlInputFieldName.IdPIssuer in record &&
	IdPSamlInputFieldName.IdPCertificate in record;

// An authentication-only connection enforces no mandatory attributes: a project with mandatory
// attributes the guide has no default for would block continue and save on rows whose IdP may
// never send them, which is the usual case with Okta.
const NO_MANDATORY_ATTRIBUTES: string[] = [];

// The Login ID is the exception, so it survives the list the validation runs on. It is required
// for every method (isMandatoryAttribute) and the OIDC exchange rejects an empty external ID
// before it ever reaches the do-not-create-user branch, so a connection saved without it would
// fail every login. Validating against an empty list would also leave the editor marking the row
// required with nothing enforcing it - the same mismatch this block exists to avoid, inverted.
// SAML never has the row at all (useMethodUserAttributes drops it), so this is empty there.
const useValidationUserAttributes = (method: Method | null) => {
	const { data: rawUserAttributes } = useMethodUserAttributes(method);
	const { data: authenticationOnly } = useIsAuthenticationOnly();

	return useMemo(
		() =>
			authenticationOnly
				? rawUserAttributes?.filter(
						(attribute) => attribute.value === EXTERNAL_ID
					)
				: rawUserAttributes,
		[rawUserAttributes, authenticationOnly]
	);
};

const useGuideValidationSchema = () => {
	const { guideType } = useIdPConfigContext();
	const guideFieldsInfo = useCurrentMethodGuideFieldsInfo();
	const currentMethod = useCurrentMethod();
	// The attribute lists have to come from the SAME configuration the mapping being validated
	// lives on. In a generic SCIM guide the mapping resolves through the effective method (the
	// enabled config) while `useCurrentMethod` still reports SAML, so reading the lists off the
	// base method would validate the OIDC mapping against the SAML config's attributes.
	// `useCurrentMethod` stays the branch selector - it names the schema shape, not the config.
	const effectiveMethod = useCurrentEffectiveMethod();
	const { data: rawMandatoryUserAttributes } =
		useMandatoryUserAttributes(effectiveMethod);
	const { data: authenticationOnly } = useIsAuthenticationOnly();
	const mandatoryUserAttributes = authenticationOnly
		? NO_MANDATORY_ATTRIBUTES
		: rawMandatoryUserAttributes;
	const userAttributes = useValidationUserAttributes(effectiveMethod);
	const { data: configsData } = useConfigs();
	const { isFeatureFlagActive } = useFeatureFlags();
	const validateCertificate = !isFeatureFlagActive(
		FeatureFlagId.SKIP_SAML_CERT_VALIDATION
	);

	const schema = useMemo(() => {
		if (guideType === GuideType.SCIM) {
			return scimValidationSchema(userAttributes || []);
		}
		if (currentMethod === Method.Saml) {
			if (areAllSamlFieldPresent(guideFieldsInfo)) {
				return samlValidationDualSchema(
					mandatoryUserAttributes || [],
					userAttributes || [],
					validateCertificate
				);
			}
			return samlValidationSingleSchema(
				mandatoryUserAttributes || [],
				userAttributes || [],
				validateCertificate
			);
		}
		if (currentMethod === Method.Xaa) {
			// XAA enforces no mandatory user attributes, so the schema takes none
			return xaaValidationSchema(
				userAttributes || [],
				Object.keys(configsData?.xaa?.jwtBearerSettings?.issuers ?? {})
			);
		}
		return oidcValidationSchema(
			mandatoryUserAttributes || [],
			userAttributes || []
		);
	}, [
		guideType,
		currentMethod,
		guideFieldsInfo,
		mandatoryUserAttributes,
		userAttributes,
		validateCertificate,
		configsData
	]);

	return useMemo(() => ({ schema }), [schema]);
};

const useCurrentStepValidationSchema = () => {
	const { guideType } = useIdPConfigContext();
	const guideFieldsInfo = useCurrentMethodGuideFieldsInfo();
	const stepFieldsInfo = useCurrentStepFieldsInfo();

	const currentMethod = useCurrentMethod();
	// see useGuideValidationSchema - the attribute lists follow the mapping's configuration
	const effectiveMethod = useCurrentEffectiveMethod();
	const { data: rawMandatoryUserAttributes } =
		useMandatoryUserAttributes(effectiveMethod);
	const { data: authenticationOnly } = useIsAuthenticationOnly();
	const mandatoryUserAttributes = authenticationOnly
		? NO_MANDATORY_ATTRIBUTES
		: rawMandatoryUserAttributes;
	const userAttributes = useValidationUserAttributes(effectiveMethod);
	const { data: configsData } = useConfigs();
	const { isFeatureFlagActive } = useFeatureFlags();
	const validateCertificate = !isFeatureFlagActive(
		FeatureFlagId.SKIP_SAML_CERT_VALIDATION
	);

	const schema = useMemo(() => {
		if (guideType === GuideType.SCIM) {
			return scimValidationSchema(userAttributes || []);
		}
		if (currentMethod === Method.Saml) {
			if (areAllSamlFieldPresent(guideFieldsInfo)) {
				if (areAllSamlFieldPresent(stepFieldsInfo)) {
					// show saml validation error on fields
					return samlValidationDualSchema(
						mandatoryUserAttributes || [],
						userAttributes || [],
						validateCertificate
					);
				}
				// dont use saml metadata validation, not all fields are present on this step
				return samlValidationLaxSchema(
					mandatoryUserAttributes || [],
					userAttributes || [],
					validateCertificate
				);
			}
			// no option to choose url or other 3
			return samlValidationStrictSchema(
				mandatoryUserAttributes || [],
				userAttributes || [],
				validateCertificate
			);
		}
		if (currentMethod === Method.Xaa) {
			// XAA enforces no mandatory user attributes, so the schema takes none
			return xaaValidationSchema(
				userAttributes || [],
				Object.keys(configsData?.xaa?.jwtBearerSettings?.issuers ?? {})
			);
		}
		return oidcValidationSchema(
			mandatoryUserAttributes || [],
			userAttributes || []
		);
	}, [
		currentMethod,
		guideFieldsInfo,
		guideType,
		stepFieldsInfo,
		mandatoryUserAttributes,
		userAttributes,
		validateCertificate,
		configsData
	]);

	return useMemo(() => ({ schema }), [schema]);
};

const isSamlMetadataValidationIssue = (issue: ZodIssue) =>
	issue.code === 'custom' && !!issue.params?.samlMetadataValidation;

const formatIssueMessage = (
	issue: ZodIssue,
	fieldsInfo: TMethodGuideFieldsInfo
) => {
	if (isSamlMetadataValidationIssue(issue)) {
		return intl.tr('ssoConfig.fields.validation.samlMetadata', {
			metadataUrlDisplayName:
				fieldsInfo[IdPSamlInputFieldName.IdPMetadataUrl]?.displayName,
			ssoUrlDisplayName:
				fieldsInfo[IdPSamlInputFieldName.IdPSsoUrl]?.displayName,
			issuerDisplayName:
				fieldsInfo[IdPSamlInputFieldName.IdPIssuer]?.displayName,
			certificateDisplayName:
				fieldsInfo[IdPSamlInputFieldName.IdPCertificate]?.displayName
		});
	}
	const params = 'params' in issue ? issue.params : {};
	return intl.tr(issue.message, {
		fieldDisplayName:
			fieldsInfo[issue.path[0] as TIdPInputField]?.displayName || issue.path[0],
		...params
	});
};

export const useFieldsValidation = (
	validationSchema: ZodObject<any> | ZodEffects<ZodObject<any>>
) => {
	const guideFieldsInfo = useCurrentMethodGuideFieldsInfo();
	const { computedFields } = useCurrentComputedFields();

	const validationErrors: TFieldsValidationErrors = useMemo(() => {
		const zParseRes = validationSchema.safeParse(computedFields);
		return Object.fromEntries(
			(zParseRes.error?.issues || []).map((issue) => [
				issue.path[0],
				{
					message: formatIssueMessage(issue, guideFieldsInfo)
				}
			])
		);
	}, [computedFields, guideFieldsInfo, validationSchema]);

	return validationErrors;
};

export const useGuideValidation = () => {
	const { schema } = useGuideValidationSchema();
	const validationErrors = useFieldsValidation(schema);
	const guideFieldsInfo = useCurrentMethodGuideFieldsInfo();

	return useMemo(() => {
		const fieldsErrors = filterObjectByKeyMask(
			validationErrors,
			guideFieldsInfo
		);
		return {
			validationErrors,
			fieldsErrors,
			fieldsUniqueErrorMessages: uniq(
				Object.entries(fieldsErrors).map(([, { message }]) => message)
			)
		};
	}, [guideFieldsInfo, validationErrors]);
};

export const useCurrentStepValidation = () => {
	const { schema } = useCurrentStepValidationSchema();
	const validationErrors = useFieldsValidation(schema);
	const stepFieldsInfo = useCurrentStepFieldsInfo();

	return useMemo(() => {
		const fieldsErrors = filterObjectByKeyMask(
			validationErrors,
			stepFieldsInfo
		);
		return {
			validationErrors,
			fieldsErrors,
			fieldsUniqueErrorMessages: uniq(
				Object.entries(fieldsErrors).map(([, { message }]) => message)
			)
		};
	}, [stepFieldsInfo, validationErrors]);
};

export const useField = (fieldName: TIdPInputField) => {
	const { validationErrors } = useCurrentStepValidation();
	const { schema } = useCurrentStepValidationSchema();
	const { getFieldValue, setFieldValue } = useSSOConfigFields();

	const setValue = useCallback(
		(newValue: any) => setFieldValue(fieldName, newValue),
		[fieldName, setFieldValue]
	);

	return {
		error: validationErrors[fieldName],
		required: !getSchemaShape(schema)[fieldName]?.isOptional(),
		value: getFieldValue(fieldName),
		setValue
	};
};

export const useSaveSSOConfigFields = () => {
	const { idpID, xaaIdpID } = useIdPConfigContext();
	const currentMethod = useCurrentMethod();
	const { computedFields } = useCurrentComputedFields();
	const { clearFields } = useSSOConfigFields();
	const { validationErrors, fieldsUniqueErrorMessages } = useGuideValidation();

	// White-label save toast for XAA (Cross-App Access); SSO/OIDC keep the ssoConfig
	// defaults by passing undefined (which falls back to useUpdateConfigs' defaults).
	const updateConfigsMutation = useUpdateConfigs(
		currentMethod === Method.Xaa
			? intl.tr('xaaConfig.save.saveSuccessMessage')
			: undefined,
		currentMethod === Method.Xaa
			? intl.tr('xaaConfig.save.saveErrorMessage')
			: undefined
	);
	const { data: configsData } = useConfigs();
	const { data: userAttributesData } = useMethodUserAttributes(currentMethod);

	const idpIdToUse = idpID || GENERIC_IDP_ID;
	// XAA remembers the selected IdP card in xaaIdpID; fall back to the generic content id.
	const xaaIdpIdToUse = xaaIdpID || GENERIC_IDP_ID;

	const save = useCallback(async () => {
		try {
			/* istanbul ignore if */
			if (!configsData || !userAttributesData) {
				return false;
			}
			let updatedConfig: IResConfigs;
			if (currentMethod === Method.Saml) {
				updatedConfig = updateQuerySamlWithFields(
					computedFields,
					configsData,
					userAttributesData,
					idpIdToUse
				);
			} else if (currentMethod === Method.Xaa) {
				// The Issuer (and Signing Algorithm) field is required by xaaValidationSchema
				// (fieldsValidation.ts: urlValidation(true)/stringValidation(true); JWKS URI is
				// optional), so the save button is gated off until an issuer key exists - the
				// no-op branch of updateQueryXaaWithFields (which returns configsData unchanged when
				// there is no issuer) is therefore unreachable from the UI. updateQueryXaaWithFields
				// either creates the issuer (fresh setup) or rewrites the existing one, preserving others.
				updatedConfig = updateQueryXaaWithFields(
					computedFields,
					configsData,
					userAttributesData,
					xaaIdpIdToUse
				);
			} else {
				updatedConfig = updateQueryOidcWithFields(
					computedFields,
					configsData,
					userAttributesData,
					idpIdToUse
				);
			}

			const enabledConfiguration = enableConfiguration(
				currentMethod,
				updatedConfig
			);
			await updateConfigsMutation.mutateAsync(enabledConfiguration);

			clearFields();
			return true;
		} catch {
			// The update mutation surfaces its own error toast; the issuer-rename collision that used to
			// throw here is now rejected up front by xaaValidationSchema (a field error on the Issuer
			// input), so there is nothing config-specific to handle in this catch.
			return false;
		}
	}, [
		configsData,
		userAttributesData,
		currentMethod,
		updateConfigsMutation,
		clearFields,
		computedFields,
		idpIdToUse,
		xaaIdpIdToUse
	]);

	return useMemo(
		() =>
			isEmpty(validationErrors)
				? { save }
				: { fieldsUniqueErrorMessages, validationErrors },
		[save, validationErrors, fieldsUniqueErrorMessages]
	);
};

export const useFieldsDirty = () => {
	const { getAllFields, clearFields } = useSSOConfigFields();

	const { computedFields } = useCurrentComputedFields();
	const { computedFields: computedFieldsWithoutUserValues } =
		useCurrentComputedFields(true);

	const currentUserFields = getAllFields();
	const isDirty = useMemo(
		() =>
			!isEmpty(currentUserFields) &&
			!isEqual(computedFields, computedFieldsWithoutUserValues),
		[computedFields, computedFieldsWithoutUserValues, currentUserFields]
	);

	return { isDirty, discard: clearFields };
};

export const useDirtyConfigs = () => {
	const { idpID, xaaIdpID } = useIdPConfigContext();
	const currentMethod = useCurrentMethod();
	const { computedFields } = useCurrentComputedFields();
	const { data: configsData } = useConfigs();
	const { data: userAttributesData } = useMethodUserAttributes(currentMethod);

	const idpIdToUse = idpID || GENERIC_IDP_ID;
	const xaaIdpIdToUse = xaaIdpID || GENERIC_IDP_ID;

	return useMemo(() => {
		if (!configsData || !userAttributesData) {
			return null;
		}
		if (currentMethod === Method.Saml) {
			return updateQuerySamlWithFields(
				computedFields,
				configsData,
				userAttributesData,
				idpIdToUse
			);
		}
		if (currentMethod === Method.Xaa) {
			// updateQueryXaaWithFields no longer throws on a rename-onto-existing collision (that is
			// rejected by xaaValidationSchema as a field error); on the collision it no-ops and returns
			// the config unchanged, so no try/catch is needed here.
			return updateQueryXaaWithFields(
				computedFields,
				configsData,
				userAttributesData,
				xaaIdpIdToUse
			);
		}
		return updateQueryOidcWithFields(
			computedFields,
			configsData,
			userAttributesData,
			idpIdToUse
		);
	}, [
		computedFields,
		configsData,
		userAttributesData,
		currentMethod,
		idpIdToUse,
		xaaIdpIdToUse
	]);
};

const STORE_VERSION = 3;

// client-secret is the only credential-typed field; localStorage is readable by
// anything running on the origin, so it must never persist there.
const SECRET_FIELD_NAMES: TIdPInputField[] = [
	IdPOidcInputFieldName.ClientSecret
];

export const stripSecretFields = (
	configs: ISSOConfigFields
): ISSOConfigFields =>
	Object.fromEntries(
		Object.entries(configs).map(([idpId, methods]) => [
			idpId,
			methods &&
				Object.fromEntries(
					Object.entries(methods).map(([method, fields]) => [
						method,
						fields && omit(fields, SECRET_FIELD_NAMES)
					])
				)
		])
	);

const StorageSync = () => {
	const { setSSOConfigs, storageLoaded, setStorageLoaded, ssoConfigs } =
		useContext(SSOConfigFieldsContext)!;

	const { data: configsData } = useConfigs();

	useEffect(() => {
		if (!configsData || storageLoaded) {
			return;
		}

		const prevSSOConfigs = loadPrevState<ISSOConfigFields>(
			StorageK.Fields,
			configsData.tenant.id,
			configsData.currentSSOId,
			STORE_VERSION
		);

		setStorageLoaded(true);
		if (prevSSOConfigs) {
			setSSOConfigs(stripSecretFields(prevSSOConfigs));
		}
	}, [configsData, setSSOConfigs, setStorageLoaded, storageLoaded]);

	useEffect(() => {
		if (!configsData || !storageLoaded) {
			return;
		}

		storeState(
			StorageK.Fields,
			configsData.tenant.id,
			configsData.currentSSOId,
			STORE_VERSION,
			stripSecretFields(ssoConfigs)
		);
	}, [configsData, ssoConfigs, storageLoaded]);

	return null;
};
